import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import CropFreeIcon from '@mui/icons-material/CropFree';
import EditIcon from '@mui/icons-material/Edit';
import MapIcon from '@mui/icons-material/Map';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Checkbox, Divider, ListItemIcon, Menu, MenuItem, TextField, Typography } from '@mui/material';
import { GridToolbarFilterButton } from '@mui/x-data-grid-premium';
import { ArrowDropDownIcon } from '@mui/x-date-pickers-pro';
import React, { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { TrackedButton as Button, TrackedIconButton as IconButton } from '../../../components/TrackedComponents';

interface QuickSearchToolbarProps {
    searchText: string;
    setSearchText: CallableFunction;
    onRefresh: CallableFunction;
    selectedMalfunctionIds: number[];
    onAssign: CallableFunction;
    setIsMapView: CallableFunction;
    isViewButtonEnabled: boolean;
    canChangeVIN: boolean;
    groupMalfunctions: boolean;
    setGroupMalfunctions: (value: boolean) => void;
    prioritizeMalfunctions: boolean;
    setPrioritizeMalfunctions: (value: boolean) => void;
}

export const QuickSearchToolbar = (props: QuickSearchToolbarProps) => {
    const { t } = useTranslation();
    const history = useHistory();
    const [actionsAnchorEl, setActionsAnchorEl] = useState<HTMLElement | null>(null);

    const handleActionsClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setActionsAnchorEl(event.currentTarget);
    };
    const handleActionsClose = () => {
        setActionsAnchorEl(null);
    };

    return (
        <Box
            sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                py: 1,
                justifyContent: 'space-between',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 1,
                }}
            >
                <TextField
                    id="table-search-field"
                    variant="standard"
                    value={props.searchText}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => props.setSearchText(e.target.value)}
                    size="small"
                    placeholder={t('setup_tool.toolbar.search')}
                    InputProps={{
                        startAdornment: <SearchIcon color="primary" fontSize="small" />,
                    }}
                />
                <GridToolbarFilterButton componentsProps={{ button: { variant: 'outlined', id: 'filters-btn' } }} />
                <IconButton id="refresh-btn" onClick={() => props.onRefresh()} sx={{ height: '32px', width: '32px' }}>
                    <RefreshIcon />
                </IconButton>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 1,
                }}
            >
                <Button
                    id="actions-btn"
                    data-testid="actions-btn"
                    variant="contained"
                    size="small"
                    sx={{ height: '32px' }}
                    onClick={handleActionsClick}
                    endIcon={<ArrowDropDownIcon />}
                >
                    {t('setup_tool.toolbar.action')}
                </Button>

                <Menu anchorEl={actionsAnchorEl} open={Boolean(actionsAnchorEl)} onClose={handleActionsClose}>
                    <MenuItem
                        id="btn-free-verify"
                        data-testid="btn-free-verify"
                        onClick={() => {
                            handleActionsClose();
                            history.push('/tasks/verify');
                        }}
                        sx={{ height: '32px' }}
                    >
                        <ListItemIcon>
                            <CropFreeIcon fontSize="small" />
                        </ListItemIcon>

                        {t('setup_tool.toolbar.verify_camera_view')}
                    </MenuItem>

                    {props.canChangeVIN ? (
                        <MenuItem
                            id="btn-change-vin"
                            data-testid="btn-change-vin"
                            onClick={() => {
                                handleActionsClose();
                                history.push('/tasks/change-vin');
                            }}
                            sx={{ height: '32px' }}
                        >
                            <ListItemIcon>
                                <EditIcon fontSize="small" />
                            </ListItemIcon>
                            {t('setup_tool.toolbar.change_vin')}
                        </MenuItem>
                    ) : null}

                    <MenuItem
                        id="btn-bulk-assign"
                        data-testid="btn-bulk-assign"
                        disabled={props.selectedMalfunctionIds.length === 0}
                        onClick={() => {
                            handleActionsClose();
                            props.onAssign();
                        }}
                        sx={{ height: '32px' }}
                    >
                        <ListItemIcon>
                            <AssignmentIndIcon fontSize="small" />
                        </ListItemIcon>
                        {t('setup_tool.toolbar.assign')}
                    </MenuItem>

                    <MenuItem
                        id="btn-map-view"
                        data-testid="btn-map-view"
                        disabled={!props.isViewButtonEnabled}
                        onClick={() => {
                            handleActionsClose();
                            props.setIsMapView(true);
                        }}
                        sx={{ height: '32px' }}
                    >
                        <ListItemIcon>
                            <MapIcon fontSize="small" />
                        </ListItemIcon>
                        {t('setup_tool.toolbar.map_view')}
                    </MenuItem>
                    <Divider component="li" />
                    <MenuItem
                        id="group-malfunctions-switch"
                        data-testid="group-malfunctions-switch"
                        disableRipple
                        sx={{ p: 0 }}
                    >
                        <Button
                            fullWidth
                            onClick={() => props.setGroupMalfunctions(!props.groupMalfunctions)}
                            startIcon={
                                <Checkbox checked={props.groupMalfunctions} size="small" disableRipple sx={{ p: 0 }} />
                            }
                            sx={{
                                justifyContent: 'flex-start',
                                textTransform: 'none',
                                px: 2,
                                py: 1,
                            }}
                            id={'group-malfunctions-switch'}
                        >
                            <Typography sx={{ fontSize: 16 }}>{t('setup_tool.toolbar.group_malfunctions')}</Typography>
                        </Button>
                    </MenuItem>

                    <MenuItem
                        id="prioritize-malfunctions-switch"
                        data-testid="prioritize-malfunctions-switch"
                        disableRipple
                        sx={{ p: 0 }}
                    >
                        <Button
                            fullWidth
                            onClick={() => props.setPrioritizeMalfunctions(!props.prioritizeMalfunctions)}
                            startIcon={
                                <Checkbox
                                    checked={props.prioritizeMalfunctions}
                                    size="small"
                                    disableRipple
                                    sx={{ p: 0 }}
                                />
                            }
                            sx={{
                                justifyContent: 'flex-start',
                                textTransform: 'none',
                                px: 2,
                                py: 1,
                            }}
                            id={'prioritize-malfunctions-switch'}
                        >
                            <Typography sx={{ fontSize: 16 }}>
                                {t('setup_tool.toolbar.prioritize_malfunctions')}
                            </Typography>
                        </Button>
                    </MenuItem>
                </Menu>
            </Box>
        </Box>
    );
};
